import React, { useState, useEffect } from "react";
import { DataTable, Filter, PageWrapper } from "../../components";
import { TransactionType } from "../../shared/enums";
import { useTranslation } from "react-i18next";
import { getTransaction } from "../../api/transaction";
import * as XLSX from 'xlsx';
import dayjs from "dayjs";
import { DATETIME_FORMAT } from "../../shared/constants";

const Transaction = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState({});
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25 });
  const [totalPages, setTotalPage] = useState(0);
  const [data, setData] = useState([]);

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 when filter changes
  };

  const columns = [
    {
      key: "createdAt",
      label: t("CREATED_AT"),
      render: (createdAt, data) => dayjs(createdAt).format(DATETIME_FORMAT),
    },
    { key: "type", label: t("TYPE") },
    { key: "currency", label: t("CURRENCY") },
    {
      key: "amount",
      label: t("AMOUNT"),
      render: (amount, data) => amount?.toFixed(2),
    },
    {
      key: "beforeBalance",
      label: t("BEFORE_BALANCE"),
      render: (beforeBalance, data) => beforeBalance?.toFixed(2),
    },
    { key: "status", label: t("STATUS") },
    { key: "fullName", label: t("FULL_NAME") },
    {
      key: "phoneNumber",
      label: t("PHONE_NUMBER"),
      render: (phoneNumber, data) => data.countryCode + data.phoneNumber,
    },
    { key: "email", label: t("EMAIL") },
    { key: "refNo", label: t("REFERENCE_NO") },
  ];

  const filters = [
    { type: "input", label: t("SEARCH"), name: "search" },
    { type: "datetime", label: t("FORM_DATE"), name: "fromDate" },
    { type: "datetime", label: t("TO_DATE"), name: "toDate" },
    {
      type: "select",
      label: t("Type"),
      name: "type",
      options: [{ label: "All", value: "" }, ...Object.keys(TransactionType).map((key) => ({
        label: t(key),
        value: TransactionType[key],
      }))],
    },
  ];

  useEffect(() => {
    getData(pagination, filter);
  }, [pagination, filter]);

  const getData = async (pagination, filter) => {
    const response = await getTransaction({
      skip: (pagination?.page - 1) * pagination?.pageSize,
      limit: pagination?.pageSize,
      ...(filter?.search ? { search: filter.search } : {}),
      ...(filter?.fromDate
        ? { fromDate: dayjs(filter.fromDate).format() }
        : {}),
      ...(filter?.toDate ? { toDate: dayjs(filter.toDate).format() } : {}),
      ...(filter?.type ? { type: filter.type } : {}),
    });
    setData(response.items);
    setTotalPage(Math.ceil(response?.pagination?.total / pagination?.pageSize));
  };

  const getExportData = () => data.map(item => ({
    [t("CREATED_AT")]: dayjs(item.createdAt).format(DATETIME_FORMAT),
    [t("TYPE")]: item.type,
    [t("CURRENCY")]: item.currency,
    [t("AMOUNT")]: item.amount?.toFixed(2),
    [t("BEFORE_BALANCE")]: item.beforeBalance?.toFixed(2),
    [t("STATUS")]: item.status,
    [t("FULL_NAME")]: item.fullName,
    [t("PHONE_NUMBER")]: item.countryCode + item.phoneNumber,
    [t("EMAIL")]: item.email,
    [t("REFERENCE_NO")]: item.refNo,
  }));

  const exportToCsv = () => {
    const worksheet = XLSX.utils.json_to_sheet(getExportData());
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  return (
    <PageWrapper
      headerProps={{
        title: "TRANSACTION",
      }}
    >
      <Filter
        filters={filters}
        onChangeFilter={handleFilterChange}
        onExport={exportToCsv}
        exportDisabled={data.length === 0}
      />
      <DataTable
        columns={columns}
        data={data}
        pagination={{ ...pagination, totalPages, onChange: setPagination }}
      />
    </PageWrapper>
  );
};

export default Transaction;
